"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Trigger = void 0;
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _inheritsLoose2 = _interopRequireDefault(require("@babel/runtime/helpers/inheritsLoose"));
var _yieldCallback = require("@zillow/yield-callback");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _identifier = require("../../util/identifier");
var _TriggerButton = require("../TriggerButton/TriggerButton");
var _excluded = ["children", "onClose", "onClosePropName", "closeWith", "triggered", "defaultOpen", "mouseEnterDelay", "mouseLeaveDelay", "onOpen", "isOpen", "isOpenPropName", "openWith", "generateDescribedBy", "generateLabelledBy", "shouldCloseOnViewportLeave", "shouldUseYieldCallback"];
/**
 * A `Trigger` is a component used to link and manage the open and close state of
 * dialog based components, such as `Tooltip`, `Popover` and `ModalDialog`.
 *
 * By default, triggers are
 * [uncontrolled components](https://reactjs.org/docs/uncontrolled-components.html), meaning
 * that they own the open and close state. Alternatively, you can provide an `isOpen` prop
 * to manage the state yourself.
 *
 * The trigger itself is a node passed to `children` that will be enhanced to listen
 * for `'click'` events by default.
 * The triggering event can be customized to listen to other events like `'mouseEnter'`
 * by changing the `openWith` prop.
 *
 * In addition to adding a trigger, you need to pass a `triggered` node that will be enhanced
 * with the trigger state. The `triggered` node will receive two props by default,
 * `isOpen` and `onClose`.
 * `isOpen` is passed the current open state of the trigger, and `onClose` is passed an event
 * handler to listen for close events from the `triggered` node.
 * The two props passed to `triggered` are named `'isOpen'` and `'onClose'` to follow
 * `Dialog` conventions, but these name can be customized
 * by changing the value of `isOpenPropName` and `onClosePropName`.
 *
 * In some cases, you will want content to close based on interaction with the trigger node
 * instead of `triggered`, for example a tooltip that shows only when you hover over the trigger.
 * For this you can use the `closeWith` prop to specify close events such as `'mouseLeave'`.
 *
 * Both trigger content and dialogs will be enhanced with appropriate aria attributes such as
 * [`aria-expanded`](https://www.w3.org/TR/wai-aria-1.1/#aria-expanded),
 * [`aria-haspopup`](https://www.w3.org/TR/wai-aria-1.1/#aria-haspopup),
 * [`aria-describedby`](https://www.w3.org/TR/wai-aria-1.1/#aria-describedby), and
 * [`aria-labelledby`](https://www.w3.org/TR/wai-aria-1.1/#aria-labelledby).
 *
 * @related ['ModalDialog', 'Popover', 'Tooltip']
 * @subcomponents ['TriggerButton', 'TriggerText']
 * @since 7.0.0
 */
var Trigger = /*#__PURE__*/function (_Component) {
  (0, _inheritsLoose2.default)(Trigger, _Component);
  function Trigger(_props) {
    var _this;
    _this = _Component.call(this, _props) || this;
    _this.onOpen = function () {
      var onOpenHandler = function onOpenHandler() {
        if (_this.props.shouldCloseOnViewportLeave) {
          _this.initializeIntersectionObserver();
        }
        if (!_this.unmounted && !_this.isClosing) {
          if (typeof _this.props.isOpen !== 'undefined') {
            _this.props.onOpen();
          } else if (!_this.state.isOpen) {
            _this.setState({
              isOpen: true
            }, function () {
              _this.props.onOpen();
            });
          }
        }
      };
      if (_this.props.shouldUseYieldCallback) {
        (0, _yieldCallback.yieldCallback)(onOpenHandler)();
      } else {
        onOpenHandler();
      }
    };
    _this.onClose = function () {
      var onCloseHandler = function onCloseHandler() {
        // The act of closing will often return focus to the trigger which is one of the possible
        // "openWith" triggers. To prevent a close/open infinite loop, we prevent an open event from
        // occurring within the same thread as a close.
        _this.clearIntersectionObserver();
        _this.isClosing = true;
        setTimeout(function () {
          _this.isClosing = false;
        }, 0);
        if (!_this.unmounted &&
        // If focus is one of the triggering events, we shouldn't close while the trigger still
        // has focus. For example, a tooltip with both hover and focus triggers should not close
        // on mouseLeave if the trigger still has focus.
        !(!_this.shouldCloseWith('click') && _this.shouldOpenWith('focus') && _this.shouldCloseWith('blur') && _this.hasFocus)) {
          if (typeof _this.props.isOpen !== 'undefined') {
            _this.props.onClose();
          } else if (_this.state.isOpen) {
            _this.setState({
              isOpen: false
            }, function () {
              _this.props.onClose();
            });
          }
        }
      };
      if (_this.props.shouldUseYieldCallback) {
        (0, _yieldCallback.yieldCallback)(onCloseHandler)();
      } else {
        onCloseHandler();
      }
    };
    _this.toggleOpen = function () {
      if (_this.state.isDelayed) {
        return null;
      }
      if (_this.props.isOpen || _this.state.isOpen) {
        return _this.onClose();
      }
      return _this.onOpen();
    };
    _this.createHandler = function (event, handler) {
      return function () {
        // handlers should never run after unmounting
        if (_this.unmounted) {
          return;
        }
        handler.apply(void 0, arguments);

        // Call the existing handler after we update our own state
        if (typeof _this.props.children !== 'function') {
          var _Children$only = _react.Children.only(_this.props.children),
            props = _Children$only.props;
          if (props && event in props) {
            props[event].apply(props, arguments);
          }
        }
      };
    };
    _this.shouldOpenWith = function (event) {
      return _this.props.openWith.indexOf(event) >= 0;
    };
    _this.shouldCloseWith = function (event) {
      return _this.props.closeWith.indexOf(event) >= 0;
    };
    _this.createIntersectionObserver = function () {
      if (!window.IntersectionObserver) {
        return;
      }
      _this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting && (_this.props.isOpen || _this.state.isOpen)) {
            _this.onClose();
          }
        });
      }, {
        rootMargin: '0px'
      });
    };
    _this.initializeIntersectionObserver = function () {
      var _this$props$children, _this$props$children$;
      if ((_this$props$children = _this.props.children) !== null && _this$props$children !== void 0 && (_this$props$children$ = _this$props$children.ref) !== null && _this$props$children$ !== void 0 && _this$props$children$.current && _this.observer) {
        _this.observer.observe(_this.props.children.ref.current);
      }
    };
    _this.clearIntersectionObserver = function () {
      if (_this.observer) {
        _this.observer.disconnect();
      }
    };
    _this.getClickHandlers = function () {
      var handlers = {};
      if (_this.shouldOpenWith('click') && !_this.shouldCloseWith('click')) {
        handlers.onClick = _this.createHandler('onClick', _this.onOpen);
      }
      if (_this.shouldOpenWith('click') && _this.shouldCloseWith('click')) {
        handlers.onClick = _this.createHandler('onClick', _this.toggleOpen);
      }
      if (_this.shouldCloseWith('click') && !_this.shouldOpenWith('click')) {
        handlers.onClick = _this.createHandler('onClick', _this.onClose);
      }
      return handlers;
    };
    _this.getContextMenuHandlers = function () {
      var handlers = {};
      if (_this.shouldOpenWith('contextMenu')) {
        handlers.onContextMenu = _this.createHandler('onContextMenu', function (e) {
          e.preventDefault();
          _this.onOpen();
        });
      }
      return handlers;
    };
    _this.getHoverHandlers = function () {
      var shouldOpen = _this.shouldOpenWith('mouseEnter');
      var shouldDelayOpen = shouldOpen && _this.props.mouseEnterDelay;
      var shouldClose = _this.shouldCloseWith('mouseLeave');
      var shouldDelayClose = shouldClose && _this.props.mouseLeaveDelay;
      var handlers = {};
      if (shouldOpen || shouldDelayClose) {
        handlers.onMouseEnter = _this.createHandler('onMouseEnter', function () {
          // Clean up mouseLeaveDelay
          if (shouldDelayClose) {
            _this.setState({
              isDelayed: false
            });
            clearTimeout(_this.mouseLeaveTimeout);
            _this.mouseLeaveTimeout = null;
          }
          if (shouldOpen) {
            if (_this.props.mouseEnterDelay) {
              if (!_this.mouseEnterTimeout) {
                _this.setState({
                  isDelayed: true
                });
                _this.mouseEnterTimeout = setTimeout(function () {
                  _this.setState({
                    isDelayed: false
                  });
                  _this.onOpen();
                }, _this.props.mouseEnterDelay);
              }
            } else {
              _this.onOpen();
            }
          }
        });
      }
      if (shouldClose || shouldDelayOpen) {
        handlers.onMouseLeave = _this.createHandler('onMouseLeave', function () {
          // Clean up mouseEnterDelay
          if (shouldDelayOpen) {
            clearTimeout(_this.mouseEnterTimeout);
            _this.mouseEnterTimeout = null;
          }
          if (shouldClose) {
            if (_this.props.mouseLeaveDelay) {
              if (!_this.mouseLeaveTimeout) {
                _this.mouseLeaveTimeout = setTimeout(_this.onClose, _this.props.mouseLeaveDelay);
              }
            } else {
              _this.onClose();
            }
          }
        });
      }
      return handlers;
    };
    _this.getFocusHandlers = function () {
      var handlers = {};
      if (_this.shouldOpenWith('focus')) {
        handlers.onFocus = _this.createHandler('onFocus', function () {
          _this.hasFocus = true;
          _this.onOpen();
        });
      }
      if (_this.shouldCloseWith('blur') || handlers.onFocus) {
        handlers.onBlur = _this.createHandler('onBlur', function () {
          _this.hasFocus = false;
          if (_this.shouldCloseWith('blur')) {
            _this.onClose();
          }
        });
      }
      return handlers;
    };
    _this.state = {
      isOpen: _props.defaultOpen,
      isDelayed: false
    };

    // Generate id if one does not already exist on triggered node
    if (_props.triggered.props && _props.triggered.props.id) {
      _this.triggeredId = _props.triggered.props.id;
    } else if (_this.props.generateDescribedBy) {
      _this.triggeredId = (0, _identifier.identifier)();
    }

    // Generate id if one does not already exist on trigger node
    if (_props.id) {
      _this.triggerId = _props.id;
    } else if (_this.props.generateLabelledBy) {
      _this.triggerId = (0, _identifier.identifier)();
    }
    return _this;
  }
  var _proto = Trigger.prototype;
  _proto.componentDidMount = function componentDidMount() {
    this.unmounted = false;
    if (this.props.shouldCloseOnViewportLeave) {
      this.createIntersectionObserver();
    }
  };
  _proto.componentWillUnmount = function componentWillUnmount() {
    this.clearIntersectionObserver();
    clearTimeout(this.mouseEnterTimeout);
    clearTimeout(this.mouseLeaveTimeout);
    this.mouseEnterTimeout = null;
    this.mouseLeaveTimeout = null;

    // Bound event handlers must also avoid setState() after unmount
    this.unmounted = true;
  };
  _proto.render = function render() {
    var _triggeredProps;
    var _this$props = this.props,
      children = _this$props.children,
      onClose = _this$props.onClose,
      onClosePropName = _this$props.onClosePropName,
      closeWith = _this$props.closeWith,
      triggered = _this$props.triggered,
      defaultOpen = _this$props.defaultOpen,
      mouseEnterDelay = _this$props.mouseEnterDelay,
      mouseLeaveDelay = _this$props.mouseLeaveDelay,
      onOpen = _this$props.onOpen,
      isOpen = _this$props.isOpen,
      isOpenPropName = _this$props.isOpenPropName,
      openWith = _this$props.openWith,
      generateDescribedBy = _this$props.generateDescribedBy,
      generateLabelledBy = _this$props.generateLabelledBy,
      shouldCloseOnViewportLeave = _this$props.shouldCloseOnViewportLeave,
      shouldUseYieldCallback = _this$props.shouldUseYieldCallback,
      rest = (0, _objectWithoutProperties2.default)(_this$props, _excluded);
    var isControlled = typeof isOpen !== 'undefined';
    var conditionalOpen = isControlled ? isOpen : this.state.isOpen;
    var triggeredProps = (_triggeredProps = {}, _triggeredProps[isOpenPropName] = conditionalOpen, _triggeredProps[onClosePropName] = this.onClose, _triggeredProps);
    var triggerProps = Object.assign({
      'aria-expanded': conditionalOpen ? 'true' : 'false'
    }, rest, this.getClickHandlers(), this.getContextMenuHandlers(), this.getHoverHandlers(), this.getFocusHandlers());

    // aria-describedby
    if (this.triggeredId && generateDescribedBy && conditionalOpen) {
      triggeredProps.id = this.triggeredId;
      triggerProps['aria-describedby'] = this.triggeredId;
    }

    // aria-labelledby
    if (this.triggerId && generateLabelledBy && conditionalOpen) {
      triggerProps.id = this.triggerId;
      triggeredProps['aria-labelledby'] = this.triggerId;
    }
    var triggeredContent;
    if (typeof triggered === 'function') {
      triggeredContent = triggered(triggeredProps, this.onClose);
    } else {
      triggeredContent = /*#__PURE__*/(0, _react.cloneElement)(triggered, triggeredProps);
    }
    var triggerContent;
    if (typeof children === 'function') {
      triggerContent = children(triggerProps);
    } else {
      triggerContent = /*#__PURE__*/(0, _react.cloneElement)(_react.Children.only(children), triggerProps);
    }
    return /*#__PURE__*/_react.default.createElement(_react.Fragment, null, triggerContent, triggeredContent);
  };
  return Trigger;
}(_react.Component);
exports.Trigger = Trigger;
Trigger.displayName = 'Trigger';
Trigger.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * An optional id that will be used on the trigger and for any aria props.
   */
  'id': _propTypes.default.string,
  /**
   * https://www.w3.org/TR/wai-aria-1.1/#aria-haspopup
   */
  'aria-haspopup': _propTypes.default.oneOf(['menu', 'dialog', 'listbox', 'false']),
  /**
   * The trigger node content or a render function.
   * By default, this will be a `TriggerButton`.
   *
   * When passed a node, the node will be cloned and passed event handlers based on the `openWith`
   * and `closeWith` props.
   * **The node must be a single element capable of accepting props (no fragments).**
   *
   * When passed a render function, the function will pass the props as a single argument in the
   * form of an object that can be spread into your node.
   */
  'children': _propTypes.default.oneOfType([_propTypes.default.element, _propTypes.default.func]),
  /**
   * Callback for changes in the open state on the trigger.
   * This is a prop passed to the `Trigger` component, as opposed to
   * `onClosePropName` which passes a prop to the `triggered` component.
   */
  'onClose': _propTypes.default.func,
  /**
   * The name of the prop that handles the `triggered` node's close event.
   * The trigger will subscribe to this callback and manage state based off it.
   */
  'onClosePropName': _propTypes.default.string,
  /**
   * The events that will trigger close, one of: `mouseLeave`, `blur`
   */
  'closeWith': _propTypes.default.arrayOf(_propTypes.default.oneOf(['click', 'mouseLeave', 'blur'])),
  /**
   * The node content or a render function activated by the trigger.
   *
   * When passed a node, the node will be cloned and passed the open state to the `isOpenPropName`
   * and the close handler to the `onClosePropName`.
   * **The node must be a single element capable of accepting props (no fragments).**
   *
   * When passed a render function, the function will pass the props as the first argument in the
   * form of an object that can be spread onto your node, and a "close" function that can be
   * called to set the open state to false.
   */
  'triggered': _propTypes.default.oneOfType([_propTypes.default.element, _propTypes.default.func]).isRequired,
  /**
   * The default open state when used as a
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  'defaultOpen': _propTypes.default.bool,
  /**
   * The open state of when used as an
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * @since 8.24.0
   */
  'isOpen': _propTypes.default.bool,
  /**
   * Delay in milliseconds before acting on the `mouseEnter` event. If `mouseLeave` occurs before
   * the time has elapsed, no action will be taken.
   */
  'mouseEnterDelay': _propTypes.default.number,
  /**
   * Delay in milliseconds before acting on the `mouseLeave` event. If `mouseEnter` occurs before
   * the time has elapsed, no action will be taken.
   */
  'mouseLeaveDelay': _propTypes.default.number,
  /**
   * Callback for changes in the open state on the trigger.
   * This is a prop passed to the `Trigger` component, as opposed to
   * `isOpenPropName` which passes a prop to the `triggered` component.
   */
  'onOpen': _propTypes.default.func,
  /**
   * The name of the prop that handles the `triggered` node's open state.
   * The trigger will manage state and set this prop on the `triggered` node.
   */
  'isOpenPropName': _propTypes.default.string,
  /**
   * The events that will trigger open, one of: `click`, `contextMenu`, `mouseEnter`, `focus`
   */
  'openWith': _propTypes.default.arrayOf(_propTypes.default.oneOf(['click', 'contextMenu', 'mouseEnter', 'focus'])),
  /**
   * Add the aria-describedby property to the trigger, automatically generating an id for the
   * triggered node if it doesn't already exist.
   */
  'generateDescribedBy': _propTypes.default.bool,
  /**
   * Add the aria-labelledby property to the triggered node, automatically generating an id for
   * the trigger if it doesn't already exist.
   */
  'generateLabelledBy': _propTypes.default.bool,
  /**
   * Close the trigger when the user scrolls away from the trigger node.
   */
  'shouldCloseOnViewportLeave': _propTypes.default.bool,
  /**
   * When `true`, the `onOpen` and `onClose` handlers will be wrapped in `yieldCallback`
   */
  'shouldUseYieldCallback': _propTypes.default.bool
} : {};
Trigger.defaultProps = {
  'id': undefined,
  'aria-haspopup': 'dialog',
  'children': /*#__PURE__*/_react.default.createElement(_TriggerButton.TriggerButton, null),
  'onClose': function onClose() {},
  'onClosePropName': 'onClose',
  'closeWith': [],
  'defaultOpen': false,
  'mouseEnterDelay': 0,
  'mouseLeaveDelay': 0,
  'onOpen': function onOpen() {},
  'isOpenPropName': 'isOpen',
  'openWith': ['click'],
  'generateLabelledBy': false,
  'generateDescribedBy': false,
  'shouldCloseOnViewportLeave': false,
  'shouldUseYieldCallback': true
};